import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface PlaceholderAfterIgnoredNodesOptions {
  placeholder: string;
  /**
   * List of node types to ignore when determining if content is empty.
   * These nodes won't be considered as "real content" for placeholder display.
   * @default ['timestamp']
   */
  ignoreNodeTypes?: string[];
}

export const PlaceholderAfterIgnoredNodes = Extension.create<PlaceholderAfterIgnoredNodesOptions>({
  name: 'placeholderAfterIgnoredNodes',

  addOptions() {
    return {
      placeholder: 'Start typing...',
      ignoreNodeTypes: ['timestamp'],
    };
  },

  addProseMirrorPlugins() {
    const { placeholder, ignoreNodeTypes = ['timestamp'] } = this.options;

    return [
      new Plugin({
        key: new PluginKey('placeholderAfterIgnoredNodes'),
        props: {
          decorations: ({ doc }) => {
            // Track ignored nodes and find where to place placeholder
            let lastIgnoredNodeEndPos = 0;
            let hasIgnoredNode = false;
            let hasRealContent = false;

            doc.descendants((node, pos) => {
              // Check if this node should be ignored
              if (ignoreNodeTypes.includes(node.type.name)) {
                hasIgnoredNode = true;
                lastIgnoredNodeEndPos = pos + node.nodeSize;
              }
              // Check if this is real text content
              else if (node.isText && node.text && node.text.trim().length > 0) {
                hasRealContent = true;
              }
              // Check if this is a non-ignored, non-text node (like mention, hardBreak, etc.)
              else if (
                !node.isText &&
                node.type.name !== 'paragraph' &&
                node.type.name !== 'doc' &&
                !ignoreNodeTypes.includes(node.type.name)
              ) {
                // Treat non-ignored inline nodes as content (e.g., mentions)
                if (node.isInline) {
                  hasRealContent = true;
                }
              }
            });

            // Show placeholder after last ignored node if we have ignored nodes but no real content
            if (hasIgnoredNode && !hasRealContent) {
              return DecorationSet.create(doc, [
                Decoration.widget(lastIgnoredNodeEndPos, () => {
                  const span = document.createElement('span');
                  span.className = 'placeholder-after-ignored';
                  span.style.cssText =
                    'pointer-events: none; user-select: none; color: var(--text-secondary, #999); font-size: 14px;';
                  span.setAttribute('data-placeholder', placeholder);
                  span.textContent = placeholder;
                  return span;
                }),
              ]);
            }

            // If no ignored nodes, show placeholder at beginning (fallback)
            if (!hasIgnoredNode && !hasRealContent && doc.childCount > 0) {
              const firstChild = doc.firstChild;
              if (firstChild && firstChild.type.name === 'paragraph') {
                return DecorationSet.create(doc, [
                  Decoration.widget(1, () => {
                    const span = document.createElement('span');
                    span.className = 'placeholder-start';
                    span.style.cssText =
                      'pointer-events: none; user-select: none; color: var(--text-secondary, #999); font-size: 14px;';
                    span.setAttribute('data-placeholder', placeholder);
                    span.textContent = placeholder;
                    return span;
                  }),
                ]);
              }
            }

            return null;
          },
        },
      }),
    ];
  },
});
